/* ===================================================================
   Contract-based eval scoring. Each check's expected output (declared in
   the eval set's answer key) is scored against the structured resultJson
   the check actually produced. Corpus-level expected final tracker state
   is scored against the run's accumulated tracker state, and divergence
   between per-email and final-state scores is surfaced as pipeline
   alignment (drift / recovery).
=================================================================== */
import type {
  AlignmentRow,
  CheckScoreDetail,
  EvalExpected,
  PerCheckScores,
  ProcessedEntry,
  SimCheck,
  TrackerMatchResult,
  TrackerMatchRow,
} from "./types";

/* Mirrors api/src/services/trackerState.ts TRACKER_KINDS — the merge keys
   the orchestrator accumulates tracker state under. */
export const TRACKER_EVAL_KINDS = [
  { checkId: "timeline", key: "timeline", resultKey: "entries", matchOn: ["date", "event"], label: "Timeline" },
  { checkId: "issues", key: "issues", resultKey: "issues", matchOn: ["issue"], label: "Open issues" },
  { checkId: "closing", key: "closingItems", resultKey: "items", matchOn: ["condition"], label: "Closing checklist" },
  { checkId: "signature", key: "signatures", resultKey: "signatures", matchOn: ["document", "party"], label: "Signature matrix" },
  { checkId: "diligence", key: "diligence", resultKey: "requests", matchOn: ["item"], label: "Diligence requests" },
  { checkId: "commitments", key: "commitments", resultKey: "commitments", matchOn: ["who", "what"], label: "Commitments" },
  { checkId: "fundsflow", key: "wires", resultKey: "wires", matchOn: ["from", "to"], label: "Wire instructions" },
] as const;

type Obj = Record<string, unknown>;
type TrackerStateLike = Record<string, Record<string, Array<Obj>>>;

const norm = (v: unknown): string =>
  String(v ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const keyOf = (e: Obj, fields: readonly string[]): string =>
  fields.map((f) => norm(e[f])).join("|");

/* Strings match by normalized containment (the model phrases freely);
   other primitives by normalized equality. Empty expectations pass. */
function primMatch(expected: unknown, got: unknown): boolean {
  const e = norm(expected);
  if (!e) return true;
  const g = norm(got);
  return typeof expected === "string" ? g.includes(e) : g === e;
}

/* An expected item matches an actual element when every primitive field of
   the expectation matches; nested objects/arrays in expectations are not
   supported and fail the item. */
function itemMatch(expected: Obj, got: Obj): boolean {
  return Object.entries(expected).every(([k, v]) =>
    v !== null && typeof v === "object" ? false : primMatch(v, got[k]),
  );
}

const summarize = (v: unknown): string => {
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > 140 ? s.slice(0, 140) + "…" : s;
};

/* Score one check's expected output against what it produced.
   Spec grammar (per key of the expected object):
   - array of objects  → each must subset-match an element of result[key]
   - array of strings  → each must appear in result[key] (or the whole
                         result when the key is absent) as a keyword
   - primitive         → must match result[key]
   A bare string/array spec is treated as keywords against the whole result
   (how stateofplay key points are scored). */
export function scoreCheckExpectation(
  spec: unknown,
  actual: SimCheck | undefined,
  emailId: number,
): { scored: number; correct: number; details: CheckScoreDetail[] } {
  const details: CheckScoreDetail[] = [];
  const ran = !!actual && !actual.skipped;
  const result = (ran && (actual!.result as Obj | null)) || null;
  const resultText = result ? norm(JSON.stringify(result)) : ran ? norm(actual!.out) : "";
  const absent = !actual ? "(check did not run)" : actual.skipped ? "(skipped)" : "(no match)";

  const keyword = (point: unknown, scopeText: string): void => {
    const ok = !!scopeText && scopeText.includes(norm(point));
    details.push({ emailId, expected: summarize(point), got: ok ? "found" : absent, ok });
  };

  const specs: Array<[string | null, unknown]> =
    spec !== null && typeof spec === "object" && !Array.isArray(spec)
      ? Object.entries(spec as Obj)
      : [[null, spec]];

  for (const [key, v] of specs) {
    const pool =
      key && result && Array.isArray(result[key]) ? (result[key] as unknown[]) : null;
    const scopeText = pool ? norm(JSON.stringify(pool)) : resultText;
    const items = Array.isArray(v) ? v : [v];
    if (!Array.isArray(v) && key && (v === null || typeof v !== "object")) {
      const ok = !!result && primMatch(v, result[key]);
      details.push({
        emailId,
        expected: `${key}: ${summarize(v)}`,
        got: ok ? summarize(result![key]) : absent,
        ok,
      });
      continue;
    }
    for (const item of items) {
      if (item !== null && typeof item === "object" && !Array.isArray(item)) {
        const hit = pool?.find(
          (g): g is Obj => !!g && typeof g === "object" && itemMatch(item as Obj, g as Obj),
        );
        details.push({
          emailId,
          expected: summarize(item),
          got: hit ? summarize(hit) : absent,
          ok: !!hit,
        });
      } else {
        keyword(item, scopeText);
      }
    }
  }
  return { scored: details.length, correct: details.filter((d) => d.ok).length, details };
}

/* Per-email per-check scores across a run, driven by each email's
   `expected.checks` answer key. */
export function computePerCheck(
  processed: ProcessedEntry[],
  expected: Record<number | string, EvalExpected> | null | undefined,
): PerCheckScores | null {
  if (!expected) return null;
  const scores: PerCheckScores = {};
  let any = false;
  for (const p of processed) {
    const ex = expected[p.email.id];
    if (!ex || !ex.checks) continue;
    for (const [checkId, spec] of Object.entries(ex.checks)) {
      any = true;
      const actual = p.checks.find((c) => c.id === checkId);
      const r = scoreCheckExpectation(spec, actual, p.email.id);
      const s = (scores[checkId] = scores[checkId] || {
        checkId,
        scored: 0,
        correct: 0,
        pct: 0,
        details: [],
      });
      s.scored += r.scored;
      s.correct += r.correct;
      s.details.push(...r.details);
    }
  }
  if (!any) return null;
  for (const s of Object.values(scores))
    s.pct = s.scored ? Math.round((s.correct / s.scored) * 100) : 0;
  return scores;
}

/* Corpus-level: expected final tracker state vs the run's accumulated
   tracker state. Keyed set match per workstream+kind — precision/recall/F1
   over normalized merge keys, plus status correctness on matched items. */
export function computeTrackerMatch(
  finalTrackers: Record<string, Record<string, Array<Obj>>> | null | undefined,
  actual: TrackerStateLike | null | undefined,
): TrackerMatchResult | null {
  if (!finalTrackers || !Object.keys(finalTrackers).length) return null;
  const rows: TrackerMatchRow[] = [];
  let totExpected = 0;
  let totMatched = 0;
  let totActual = 0;
  for (const [ws, kinds] of Object.entries(finalTrackers)) {
    for (const [kindKey, expEntries] of Object.entries(kinds || {})) {
      const def = TRACKER_EVAL_KINDS.find((d) => d.key === kindKey || d.checkId === kindKey);
      if (!def || !Array.isArray(expEntries)) continue;
      const actEntries = (actual?.[ws]?.[def.key] || []).filter(
        (a): a is Obj => !!a && typeof a === "object",
      );
      const used = new Set<number>();
      let matched = 0;
      let statusN = 0;
      let statusOK = 0;
      for (const e of expEntries) {
        const idx = actEntries.findIndex(
          (a, i) =>
            !used.has(i) &&
            (keyOf(a, def.matchOn) === keyOf(e, def.matchOn) ||
              def.matchOn.every((f) => primMatch(e[f], a[f]))),
        );
        if (idx === -1) continue;
        used.add(idx);
        matched++;
        if (e.status != null) {
          statusN++;
          if (primMatch(e.status, actEntries[idx].status)) statusOK++;
        }
      }
      const expectedN = expEntries.length;
      const actualN = actEntries.length;
      const p = actualN ? matched / actualN : 0;
      const r = expectedN ? matched / expectedN : 0;
      const f1 = p + r > 0 ? (2 * p * r) / (p + r) : 0;
      rows.push({
        ws,
        kind: def.key,
        label: def.label,
        expectedN,
        matchedN: matched,
        extraN: actualN - matched,
        statusN,
        statusOK,
        f1: Math.round(f1 * 100) / 100,
      });
      totExpected += expectedN;
      totMatched += matched;
      totActual += actualN;
    }
  }
  if (!rows.length) return null;
  const precision = totActual ? totMatched / totActual : 0;
  const recall = totExpected ? totMatched / totExpected : 0;
  const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
  return {
    rows,
    precision: Math.round(precision * 100) / 100,
    recall: Math.round(recall * 100) / 100,
    f1: Math.round(f1 * 100) / 100,
    pct: Math.round(f1 * 100),
  };
}

/* Pipeline alignment: a tracker whose per-email outputs score well but
   whose final accumulated state does not (or vice versa) indicates
   upstream-induced drift (or recovery) rather than a bad extractor. */
export function computeAlignment(
  perCheck: PerCheckScores | null | undefined,
  trackerMatch: TrackerMatchResult | null | undefined,
): AlignmentRow[] | null {
  if (!trackerMatch) return null;
  const rows: AlignmentRow[] = [];
  for (const def of TRACKER_EVAL_KINDS) {
    const finals = trackerMatch.rows.filter((r) => r.kind === def.key);
    if (!finals.length) continue;
    const fE = finals.reduce((s, r) => s + r.expectedN, 0);
    const fM = finals.reduce((s, r) => s + r.matchedN, 0);
    const finalPct = fE ? Math.round((fM / fE) * 100) : null;
    const pc = perCheck?.[def.checkId];
    const perEmailPct = pc ? pc.pct : null;
    const verdict: AlignmentRow["verdict"] =
      perEmailPct !== null && finalPct !== null
        ? perEmailPct >= 80 && finalPct < 80
          ? "drift"
          : perEmailPct < 80 && finalPct >= 80
            ? "recovered"
            : "aligned"
        : "aligned";
    rows.push({ checkId: def.checkId, label: def.label, perEmailPct, finalPct, verdict });
  }
  return rows.length ? rows : null;
}
